import { Component, inject, type OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
    AuthServerProvider,
    type MfaRequired,
    StateStorageService,
} from "../../core";
import { saveOAuthSession } from "../../core/auth/oauth-storage.util";
import { OAuthHandoffService } from "../../core/login/oauth-handoff.service";
import {
    EmitEvent,
    Event,
    EventService,
} from "../../shared/events/events.service";

@Component({
    templateUrl: "./oauth-callback.component.html",
    standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
})
export class OAuthCallbackComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly authServerProvider = inject(AuthServerProvider);
    private readonly stateStorageService = inject(StateStorageService);
    private readonly eventService = inject(EventService);
    private readonly oauthHandoffService = inject(OAuthHandoffService);

    ngOnInit(): void {
        const code = this.route.snapshot.queryParams["oauth_code"];
        const error = this.route.snapshot.queryParams["oauth_error"];

        if (error) {
            this.redirectToLoginWithError(decodeURIComponent(error));
            return;
        }

        if (!code) {
            this.redirectToLoginWithError("No authorization code received.");
            return;
        }

        this.exchangeCode(code);
    }

    private exchangeCode(code: string): void {
        this.authServerProvider.exchangeOAuthCode(code).subscribe({
            next: (data) => this.handleAuthSuccess(data),
            error: (err) => {
                console.error("OAuth token exchange failed", err);
                this.redirectToLoginWithError(
                    "Authentication failed. Please try again.",
                );
            },
        });
    }

    private handleAuthSuccess(data: MfaRequired | null): void {
        // Store OAuth provider so logout can redirect to the provider's logout endpoint,
        // even when MFA is required (since the shared 2FA endpoint doesn't carry provider info in the JWT).
        const provider = this.route.snapshot.queryParams["oauth_provider"];
        const login_hint = this.route.snapshot.queryParams["oauth_login_hint"];
        saveOAuthSession(provider, login_hint);

        if (data?.verifyTwoFactor && data?.username) {
            this.router.navigate(["/login", "verify"], {
                queryParams: { mfaType: data.mfaType, username: data.username },
                replaceUrl: true,
            });
        } else if (this.oauthHandoffService.hasPendingHandoff()) {
            this.performOAuthHandoff();
        } else {
            this.eventService.emit(new EmitEvent(Event.REFRESH_NAV, "true"));

            const redirect = this.stateStorageService.getUrl();
            if (redirect) {
                this.stateStorageService.storeUrl(null);
                this.router.navigateByUrl(redirect, { replaceUrl: true });
            } else {
                this.router.navigate(["/"], { replaceUrl: true });
            }
        }
    }

    private performOAuthHandoff(): void {
        this.oauthHandoffService.completeHandoff().subscribe({
            error: (err) => {
                console.error("OAuth handoff failed", err);
                this.redirectToLoginWithError(
                    "OAuth handoff failed. Please try again.",
                );
            },
        });
    }

    private redirectToLoginWithError(message: string): void {
        this.router.navigate(["/login"], {
            state: { oauth_error: message },
            replaceUrl: true,
        });
    }
}
